import { Camera, MapPin, Mountain, Coffee, RotateCcw, AlertTriangle, Eye, Flag } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WaypointInfo {
  id: string;
  name: string;
  description?: string;
  detection_type?: string;
  photo_urls?: string[];
  recorded_at?: number;
  elapsedSeconds?: number;
}

interface WaypointInfoPanelProps {
  waypoint: WaypointInfo | null;
  isVisible: boolean;
  onPhotoClick?: (url: string) => void;
  className?: string;
}

export const getWaypointIcon = (detectionType?: string) => {
  switch (detectionType) {
    case "photo_cluster":
      return <Camera className="h-4 w-4" />;
    case "rest_stop":
    case "stop":
      return <Coffee className="h-4 w-4" />;
    case "turnaround":
      return <RotateCcw className="h-4 w-4" />;
    case "climb":
    case "elevation_change":
      return <Mountain className="h-4 w-4" />;
    case "hazard":
    case "obstacle":
      return <AlertTriangle className="h-4 w-4" />;
    case "scenic":
      return <Eye className="h-4 w-4" />;
    case "landmark":
      return <Flag className="h-4 w-4" />;
    default:
      return <MapPin className="h-4 w-4" />;
  }
};

export const getWaypointLabel = (detectionType?: string) => {
  switch (detectionType) {
    case "photo_cluster":
      return "Photo Stop";
    case "rest_stop":
      return "Rest Stop";
    case "stop":
      return "Stop";
    case "turnaround":
      return "Turnaround";
    case "climb":
      return "Climb";
    case "elevation_change":
      return "Elevation Change";
    case "hazard":
      return "Hazard";
    case "obstacle":
      return "Obstacle";
    case "scenic":
      return "Scenic Spot";
    case "landmark":
      return "Landmark";
    default:
      return "Waypoint";
  }
};

const formatElapsedTime = (seconds: number) => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${secs}s`;
};

export const WaypointInfoPanel = ({
  waypoint,
  isVisible,
  onPhotoClick,
  className,
}: WaypointInfoPanelProps) => {
  if (!waypoint || !isVisible) return null;

  const photoUrl = waypoint.photo_urls?.[0];
  const hasMultiplePhotos = (waypoint.photo_urls?.length || 0) > 1;

  return (
    <div
      className={cn(
        "absolute bottom-20 left-3 right-3 md:left-3 md:right-auto md:max-w-xs",
        "bg-background/95 backdrop-blur-sm rounded-lg shadow-lg border border-border",
        "transition-all duration-300 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        className
      )}
    >
      <div className="p-3">
        {/* Header with icon and type */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary">
            {getWaypointIcon(waypoint.detection_type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              {getWaypointLabel(waypoint.detection_type)}
            </p>
            <p className="text-sm font-semibold text-foreground truncate">
              {waypoint.name}
            </p>
          </div>
          {waypoint.elapsedSeconds !== undefined && (
            <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              {formatElapsedTime(waypoint.elapsedSeconds)}
            </div>
          )}
        </div>

        {/* Content area with optional photo */}
        <div className="flex gap-3">
          {photoUrl && (
            <button
              onClick={() => onPhotoClick?.(photoUrl)}
              className="relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden hover:opacity-90 transition-opacity"
            >
              <img
                src={photoUrl}
                alt={waypoint.name}
                className="w-full h-full object-cover"
              />
              {hasMultiplePhotos && (
                <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                  +{(waypoint.photo_urls?.length || 1) - 1}
                </div>
              )}
            </button>
          )}
          {waypoint.description && (
            <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
              {waypoint.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
